import React, { useMemo } from 'react'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { FragmentType, graphql, useFragment } from '../../../gql'
import { Table } from './Table'
import { ProductCell } from './ProductCell'
import { SquareAmountCell } from './SquareAmountCell'
import { EditReturnItemModal, ReturnFormData } from './EditReturnItemModal'
import { FragmentValue } from '../../../lib/RequestUtils'

export const OrderReturnsTable_OrderItemFragment = graphql(`
  fragment OrderReturnsTable_OrderItemFragment on OrderItem {
    id
    name
    code
    ean
    quantity
    type
    product {
      code
      ean
      ean2
      ean3
      ean4
      ean5
      bundleChildren {
        child {
          ean2
          ean3
          ean4
          ean5
          ...ProductCell_ProductFragment
        }
      }
      ...ProductCell_ProductFragment
    }
    ...EditReturnItemModal_OrderItemFragment
  }
`)

interface OrderReturnsTableProps {
  items: (FragmentType<typeof OrderReturnsTable_OrderItemFragment> & {
    quantityReturned: number
    quantityAvailableToReturn: number
  })[]
  orderId: number
  defaultDepotId?: number
  onAddReturn: (data: ReturnFormData) => void
  depots: { id: number; name: string }[]
}

const columnHelper = createColumnHelper<
  FragmentValue<typeof OrderReturnsTable_OrderItemFragment> & {
    quantityReturned: number
    quantityAvailableToReturn: number
    product: { isBundleChild: boolean }
  }
>()

export const OrderReturnsTable: React.FC<OrderReturnsTableProps> = ({
  items,
  orderId,
  defaultDepotId,
  onAddReturn,
  depots,
}) => {
  'use no memo'

  const unmaskedItems = useFragment(OrderReturnsTable_OrderItemFragment, items)

  const orderItemsWithBundleChildren = useMemo(
    () => unmaskedItems.filter(item => item.type === 'product'),
    [unmaskedItems],
  )

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'product',
        header: 'Název zboží',
        cell: ({ row }) =>
          row.original.product ? (
            <ProductCell product={row.original.product} />
          ) : (
            <div className="font-semibold">{row.original.name}</div>
          ),
        // @ts-ignore
        size: '2fr',
      }),
      columnHelper.accessor('product.code', {
        header: 'Kód',
        cell: ({ getValue }) => getValue() || '-',
        size: 250,
      }),
      columnHelper.accessor('product.ean', {
        header: 'EAN',
        cell: ({ getValue }) => getValue() || '-',
        size: 150,
      }),
      columnHelper.accessor('quantity', {
        header: () => <div className="text-center">Objednáno (ks)</div>,
        cell: ({ getValue }) => <SquareAmountCell>{getValue()}</SquareAmountCell>,
        size: 120,
      }),
      columnHelper.accessor('quantityAvailableToReturn', {
        header: () => <div className="text-center">Možno vrátit</div>,
        cell: ({ getValue }) => <SquareAmountCell className="bg-yellow">{getValue()}</SquareAmountCell>,
        size: 120,
      }),
      columnHelper.accessor('quantityReturned', {
        header: () => <div className="text-center">Vráceno</div>,
        cell: ({ getValue }) => <SquareAmountCell className="bg-green">{getValue()}</SquareAmountCell>,
        size: 120,
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <div className="text-center">Vrátit</div>,
        cell: ({ row }) => (
          <div className="flex justify-center flex-1">
            <EditReturnItemModal
              orderItem={row.original}
              orderId={orderId}
              quantityReturned={row.original.quantityReturned}
              defaultDepotId={defaultDepotId}
              onConfirm={onAddReturn}
              disabled={row.original.quantity <= 0}
              depots={depots}
            />
          </div>
        ),
        size: 100,
      }),
    ],
    [orderId, defaultDepotId, onAddReturn, depots],
  )

  const columnVisibility = useMemo(
    () => ({
      code: !orderItemsWithBundleChildren.every(item => !item.product?.code),
      ean: !orderItemsWithBundleChildren.every(item => !item.product?.ean),
    }),
    [orderItemsWithBundleChildren],
  )

  const tableState = useMemo(
    () => ({
      columnVisibility,
    }),
    [columnVisibility],
  )

  const table = useReactTable({
    columns,
    data: orderItemsWithBundleChildren,
    getCoreRowModel: getCoreRowModel(),
    state: tableState,
    defaultColumn: {
      size: 'auto' as any,
    },
  })

  return <Table table={table} />
}
